import {
	AdminViewOrderWrapperPage,
	FrontendContentPage,
	Severity,
	SystemRole,
} from "node-library/dist/constants";
import {
	FrontendAdminViewSampleWithInstanceAndSpec,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { sendGETRequest } from "../../requestHelper/sendRequest";

import AdminInstanceDetailsContent from "../mainPages/instanceDetailsPage/adminInstanceDetailsContent";
import OrderDetailsContentSkeletonUI from "../mainPages/viewOrders/skeletonUI/orderDetailsContentSkeleton";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	instanceId: string;
	user: FrontendUserInfo | undefined;

	navigateToOrderDetailsPage: (
		orderId: string,
		fromPage: FrontendContentPage,
	) => void;
	goBackToPreviousPage: (reload: boolean) => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewInstanceDetailsContentWrapper(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		instanceId,
		goBackToPreviousPage,
		navigateToOrderDetailsPage,
		setCompany,
		setUnsavedChanges,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);

	const [selectedInstance, setSelectedInstance] =
		useState<FrontendAdminViewSampleWithInstanceAndSpec>();

	const [currentPageIndex, setCurrentPageIndex] =
		useState<AdminViewOrderWrapperPage>(
			AdminViewOrderWrapperPage.ORDER_DETAILS,
		);
	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	const doesUserHaveAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.sampleInstances.adminGet,
			)
		: false;

	useEffect(() => {
		async function loadInstanceDetails() {
			setLoading(true);
			try {
				const response = await sendGETRequest(
					"/sample-instances/admin-get-instance-details",
					{
						instanceId,
					},
				);
				const frontendAdminInstance: FrontendAdminViewSampleWithInstanceAndSpec =
					response?.data?.frontendAdminInstance;
				setSelectedInstance(frontendAdminInstance);
				const specDirectories: Array<FrontendSampleSpecificationDirectory> =
					response?.data?.specDirectories;
				setSpecDirectories(specDirectories);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			} finally {
				setLoading(false);
			}
		}

		if (instanceId && doesUserHaveAccess) {
			loadInstanceDetails();
		}
	}, [instanceId]);

	function gotoInstanceDetailsPage() {
		setCurrentPageIndex(0);
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = !selectedInstance ? (
				<OrderDetailsContentSkeletonUI />
			) : (
				<AdminInstanceDetailsContent
					company={props.company as FrontendCompanyWithAdditionalInfomation}
					instance={
						selectedInstance as FrontendAdminViewSampleWithInstanceAndSpec
					}
					user={props.user as FrontendUserInfo}
					navigateToOrderDetailsPage={navigateToOrderDetailsPage}
					goBackToPreviousPage={goBackToPreviousPage}
					setCompany={setCompany}
					setInstance={setSelectedInstance}
					specDirectories={specDirectories}
				></AdminInstanceDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{doesUserHaveAccess ? (
				<>{currentComponent}</>
			) : (
				<div>{translation("YouDoNotHavePermissionToViewContent")}</div>
			)}
		</div>
	);
}
